import type { ItemType, RewardBundle, SeasonInfo } from '@vm/shared';

/**
 * Battle pass seasons. Rules: the free track pays coins, a few gems, coin-shop cosmetics and some
 * pass-exclusive cosmetics; the premium track (bought with gems) pays cosmetics (incl. the
 * pass-exclusive camos/effects/banners/pet styles) and gems only — never coins, weapons, gadgets,
 * pets or XP boosts, so buying it never buys power.
 */
const TIERS = 50;
const XP_PER_TIER = 2000;
const PREMIUM_PRICE_GEMS = 800;

const item = (type: ItemType, id: string): RewardBundle => ({ items: [{ type, id }] });

const S1_FREE_ITEMS: Record<number, RewardBundle> = {
  5: item('armor', 'urban'),
  8: item('petstyle', 'camo'),
  10: item('camo', 'hazard'),
  15: item('armor', 'desert'),
  25: item('armor', 'scout'),
  30: item('deathfx', 'slime_bubbles'),
  35: item('skin', 'engineer'),
  45: item('banner', 'gamer'),
  50: item('skin', 'ninja'),
};

const S1_PREMIUM_ITEMS: Record<number, RewardBundle> = {
  1: item('armor', 'crimson'),
  6: item('skin', 'robot'),
  10: item('camo', 'aurora'),
  12: item('armor', 'cobalt'),
  16: item('banner', 'starlight'),
  18: item('deathfx', 'confetti'),
  22: item('camo', 'chrome'),
  24: item('armor', 'toxic'),
  28: item('deathfx', 'neon_glitch'),
  30: item('skin', 'pirate'),
  34: item('petstyle', 'neon'),
  36: item('armor', 'jungle'),
  40: item('camo', 'neon_grid'),
  42: item('armor', 'neon'),
  46: item('skin', 'astronaut'),
  50: item('armor', 'void'),
};

function freeReward(tier: number): RewardBundle | null {
  if (S1_FREE_ITEMS[tier]) return S1_FREE_ITEMS[tier];
  if (tier % 10 === 0) return { gems: 15 };
  if (tier % 2 === 1) return { coins: Math.round((150 + 10 * tier) / 50) * 50 };
  return null;
}

function premiumReward(tier: number): RewardBundle | null {
  if (S1_PREMIUM_ITEMS[tier]) return S1_PREMIUM_ITEMS[tier];
  return { gems: tier % 5 === 0 ? 30 : 15 };
}

export const SEASON_1: SeasonInfo = {
  id: 's1',
  name: 'Season 1: Voxel Dawn',
  startsAt: '2026-09-01T00:00:00.000Z',
  endsAt: '2026-12-01T00:00:00.000Z',
  tiers: TIERS,
  xpPerTier: XP_PER_TIER,
  premiumPriceGems: PREMIUM_PRICE_GEMS,
  rewards: Array.from({ length: TIERS }, (_, i) => ({ tier: i + 1, free: freeReward(i + 1), premium: premiumReward(i + 1) })),
};

/** All seasons in chronological order. */
export const SEASONS: SeasonInfo[] = [SEASON_1];

/** The season running at `now`, or null between seasons. */
export function activeSeason(now: Date, seasons: readonly SeasonInfo[] = SEASONS): SeasonInfo | null {
  const t = now.getTime();
  return seasons.find((s) => Date.parse(s.startsAt) <= t && t < Date.parse(s.endsAt)) ?? null;
}

/** The active season, else the most recently started one, else the first upcoming one. */
export function currentSeason(now: Date, seasons: readonly SeasonInfo[] = SEASONS): SeasonInfo {
  const active = activeSeason(now, seasons);
  if (active) return active;
  const t = now.getTime();
  const started = seasons.filter((s) => Date.parse(s.startsAt) <= t);
  return started[started.length - 1] ?? seasons[0];
}

/** Highest tier unlocked with `xp` battle pass XP. Tier 1 is unlocked from the start. */
export function bpTier(season: Pick<SeasonInfo, 'tiers' | 'xpPerTier'>, xp: number): number {
  return Math.min(season.tiers, 1 + Math.floor(Math.max(0, xp) / season.xpPerTier));
}

export function seasonReward(season: SeasonInfo, tier: number, track: 'free' | 'premium'): RewardBundle | null {
  const row = season.rewards.find((r) => r.tier === tier);
  return row ? row[track] : null;
}
